// Tier 1 profile customization — structured, allow-listed theme tokens.
//
// This is the "no code" tier of MySpace-style profile customization: users pick
// from a bounded set of safe options (colors, a gradient, a font, a card style)
// that BOTH the web client and the native mobile app can render with their own
// components. There is no user HTML/CSS here, so there is no injection surface —
// every field is validated against a hex pattern or an allow-list before it is
// stored or rendered.
//
// Pure data only (no react / no schema imports) so it can be consumed by Metro
// on mobile, Vite on web, and the dev Express server alike.

export const PROFILE_FONT_PRESETS = ["default", "serif", "mono", "rounded"] as const;
export type ProfileFontPreset = (typeof PROFILE_FONT_PRESETS)[number];

// "default" = standard Scroll profile, "yourspace" = the immersive MySpace layout
// (web). The legacy "glass" value migrates to "yourspace"; the retired "solid"/"cover"
// card treatments fall back to "default". A cover PHOTO is now a separate, universal
// option (see ProfileTheme.coverImageUrl) — any profile can have one, independent of
// cardStyle.
export const PROFILE_CARD_STYLES = ["default", "yourspace"] as const;
export type ProfileCardStyle = (typeof PROFILE_CARD_STYLES)[number];

/**
 * Normalize a stored cardStyle value for reading: migrate the legacy "glass" value
 * to "yourspace" and drop anything not in the allow-list (so retired "solid"/"cover"
 * values fall back to the standard layout). Single source of truth so the migration
 * isn't duplicated across render/editor call sites; the write-side sanitizers mirror it.
 */
export function normalizeCardStyle(value: unknown): ProfileCardStyle | undefined {
  const migrated = value === "glass" ? "yourspace" : value;
  return typeof migrated === "string" && (PROFILE_CARD_STYLES as readonly string[]).includes(migrated)
    ? (migrated as ProfileCardStyle)
    : undefined;
}

/** True when a theme uses the immersive YourSpace (MySpace) layout (legacy "glass" included). */
export function isYourSpaceStyle(theme: ProfileTheme | null | undefined): boolean {
  return normalizeCardStyle(theme?.cardStyle) === "yourspace";
}

export interface ProfileGradient {
  from: string;
  to: string;
  /** Direction in degrees, 0–360. Defaults to 135 when omitted. */
  angle?: number;
}

export interface ProfileTheme {
  /** Solid page background behind the profile. */
  backgroundColor?: string | null;
  /** Two-stop gradient page background. Takes precedence over backgroundColor. */
  backgroundGradient?: ProfileGradient | null;
  /** Accent for links/buttons on the profile; overrides the user's global accentColor. */
  accentColor?: string | null;
  /** Hero text color, for legibility over a custom background. */
  textColor?: string | null;
  /** Surface treatment for the identity card / hero cluster. */
  cardStyle?: ProfileCardStyle;
  /** Display font for the profile hero. */
  font?: ProfileFontPreset;
  /** Cover ("banner") photo URL — only a Vercel Blob URL is accepted. Universal: any profile can set one. */
  coverImageUrl?: string | null;
  /** Vertical framing of the cover photo, as an object-position percentage (0=top, 100=bottom). Default 50. */
  coverPosition?: number | null;
  /** Zoom factor for the cover photo (1 = fit, up to 3). Default 1. */
  coverScale?: number | null;
}

/** CSS font stacks for each preset (web). Mobile maps presets to loaded fonts. */
export const PROFILE_FONT_STACKS: Record<ProfileFontPreset, string> = {
  default: "inherit",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'SF Mono', ui-monospace, 'Cascadia Code', Menlo, monospace",
  rounded: "'Nunito', ui-rounded, 'SF Pro Rounded', system-ui, sans-serif",
};

// A few curated starting points so users can pick a full look in one tap.
export const PROFILE_BACKGROUND_PRESETS: { id: string; label: string; gradient: ProfileGradient }[] = [
  { id: "sunset", label: "Sunset", gradient: { from: "#FF6B6B", to: "#FFD93D", angle: 135 } },
  { id: "ocean", label: "Ocean", gradient: { from: "#2563EB", to: "#06B6D4", angle: 135 } },
  { id: "forest", label: "Forest", gradient: { from: "#059669", to: "#4D7C0F", angle: 135 } },
  { id: "grape", label: "Grape", gradient: { from: "#7C3AED", to: "#EC4899", angle: 135 } },
  { id: "midnight", label: "Midnight", gradient: { from: "#0F172A", to: "#334155", angle: 135 } },
  { id: "mono", label: "Mono", gradient: { from: "#1D1916", to: "#57534E", angle: 135 } },
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function cleanHex(value: unknown): string | null {
  return typeof value === "string" && HEX_RE.test(value) ? value.toLowerCase() : null;
}

function cleanGradient(value: unknown): ProfileGradient | null {
  if (!value || typeof value !== "object") return null;
  const g = value as Record<string, unknown>;
  const from = cleanHex(g.from);
  const to = cleanHex(g.to);
  if (!from || !to) return null;
  const out: ProfileGradient = { from, to };
  if (typeof g.angle === "number" && Number.isFinite(g.angle)) {
    out.angle = Math.min(360, Math.max(0, Math.round(g.angle)));
  }
  return out;
}

// Only accept our own Vercel Blob URLs for the cover photo — never an arbitrary
// external URL, which in an <img> would be an injection / privacy-leak vector.
const BLOB_URL_RE = /^https:\/\/[a-z0-9.-]+\.blob\.vercel-storage\.com\/[^\s"'<>]+$/i;
function cleanCoverUrl(value: unknown): string | null {
  return typeof value === "string" && BLOB_URL_RE.test(value) ? value : null;
}
/** Vertical framing 0–100 (object-position %); defaults to 50 (center) for bad input. */
function cleanCoverPosition(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(100, Math.max(0, Math.round(value)))
    : 50;
}
/** Zoom 1–3 (2-dp); null for bad input. */
function cleanCoverScale(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.min(3, Math.max(1, Math.round(value * 100) / 100));
}

/**
 * Coerce arbitrary client input into a safe ProfileTheme. Unknown keys are
 * dropped, colors must be #rrggbb, enums must be in their allow-list. Returns
 * `null` when nothing valid remains (so the column can be cleared).
 */
export function sanitizeProfileTheme(input: unknown): ProfileTheme | null {
  if (input === null) return null;
  if (!input || typeof input !== "object") return null;
  const raw = input as Record<string, unknown>;
  const theme: ProfileTheme = {};

  const backgroundColor = cleanHex(raw.backgroundColor);
  if (backgroundColor) theme.backgroundColor = backgroundColor;

  const backgroundGradient = cleanGradient(raw.backgroundGradient);
  if (backgroundGradient) theme.backgroundGradient = backgroundGradient;

  const accentColor = cleanHex(raw.accentColor);
  if (accentColor) theme.accentColor = accentColor;

  const textColor = cleanHex(raw.textColor);
  if (textColor) theme.textColor = textColor;

  // Migrate retired cardStyle values ("glass"→"yourspace", "solid"→"cover").
  const cardStyle = normalizeCardStyle(raw.cardStyle);
  if (cardStyle) theme.cardStyle = cardStyle;

  if (typeof raw.font === "string" && (PROFILE_FONT_PRESETS as readonly string[]).includes(raw.font)) {
    theme.font = raw.font as ProfileFontPreset;
  }

  // Cover photo + framing. Position/scale are only kept alongside a valid image,
  // and only when non-default, so the stored theme stays minimal.
  const coverImageUrl = cleanCoverUrl(raw.coverImageUrl);
  if (coverImageUrl) {
    theme.coverImageUrl = coverImageUrl;
    const coverPosition = cleanCoverPosition(raw.coverPosition);
    if (coverPosition !== 50) theme.coverPosition = coverPosition;
    const coverScale = cleanCoverScale(raw.coverScale);
    if (coverScale && coverScale !== 1) theme.coverScale = coverScale;
  }

  return Object.keys(theme).length > 0 ? theme : null;
}

/** CSS `background` shorthand value for a theme, or null when no background is set. */
export function profileThemeBackgroundCss(theme: ProfileTheme | null | undefined): string | null {
  if (!theme) return null;
  if (theme.backgroundGradient) {
    const { from, to, angle = 135 } = theme.backgroundGradient;
    return `linear-gradient(${angle}deg, ${from}, ${to})`;
  }
  if (theme.backgroundColor) return theme.backgroundColor;
  return null;
}

/** Perceived (sRGB-weighted) luminance 0–1 for a #rrggbb hex, or null if unparseable. */
function hexLuminance(hex: string): number | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Readable hero text colors over a themed background.
const PROFILE_TEXT_ON_DARK = "#FFFFFF";
const PROFILE_TEXT_ON_LIGHT = "#1D1916";

/**
 * Readable hero text color for a theme. An explicit `textColor` always wins;
 * otherwise it is derived from the background's luminance (light text on dark
 * backgrounds, dark text on light) so custom backgrounds stay legible. Returns
 * `null` when no background is set, so callers fall back to the platform default.
 */
export function profileThemeTextColor(theme: ProfileTheme | null | undefined): string | null {
  if (!theme) return null;
  if (theme.textColor) return theme.textColor;
  const stops: string[] = [];
  if (theme.backgroundColor) stops.push(theme.backgroundColor);
  if (theme.backgroundGradient) stops.push(theme.backgroundGradient.from, theme.backgroundGradient.to);
  const lums = stops.map(hexLuminance).filter((x): x is number => x !== null);
  if (lums.length === 0) return null;
  const avg = lums.reduce((sum, l) => sum + l, 0) / lums.length;
  return avg < 0.5 ? PROFILE_TEXT_ON_DARK : PROFILE_TEXT_ON_LIGHT;
}

/** True when the resolved hero text for a theme is light (for dimmed/secondary variants and borders). */
export function profileThemeIsLightText(theme: ProfileTheme | null | undefined): boolean {
  const color = profileThemeTextColor(theme);
  if (!color) return false;
  // Use the same ≥0.5 boundary as profileColorIsLight so the two never disagree.
  return profileColorIsLight(color);
}

/** True when a #rrggbb color reads as light (perceived luminance ≥ 0.5). Unparseable → false. */
export function profileColorIsLight(hex: string): boolean {
  const lum = hexLuminance(hex);
  return lum !== null && lum >= 0.5;
}
